"use client";

import { useRef, useEffect } from "react";
import type { ReactNode } from "react";

interface ResponsiveWidgetWrapperProps {
  children: ReactNode;
  designWidth: number;
  designHeight: number;
  className?: string;
}

/**
 * A wrapper component that scales its children proportionally based on container width.
 * Uses CSS media queries to apply styles - JavaScript only calculates CSS variable values.
 * This prevents flash on desktop because the media query doesn't match, so variables are set but not used.
 */
export function ResponsiveWidgetWrapper({
  children,
  designWidth,
  designHeight,
  className = "",
}: ResponsiveWidgetWrapperProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const updateCSSVariables = () => {
      const containerWidth = container.offsetWidth;
      const scale = Math.min(1, containerWidth / designWidth);
      const shadowPadding = 32;
      const adjustedHeight = designHeight * scale + shadowPadding * 2;

      // Only set CSS variables - CSS media query decides if they're used
      container.style.setProperty("--rww-scale", scale.toString());
      container.style.setProperty("--rww-height", `${adjustedHeight}px`);
      container.style.setProperty("--rww-padding", `${shadowPadding}px`);
      container.style.setProperty("--rww-width", `${designWidth}px`);
      container.style.setProperty("--rww-inner-height", `${designHeight}px`);
    };

    updateCSSVariables();

    const resizeObserver = new ResizeObserver(updateCSSVariables);
    resizeObserver.observe(container);

    return () => resizeObserver.disconnect();
  }, [designWidth, designHeight]);

  return (
    <div ref={containerRef} className={`responsive-widget-container ${className}`}>
      <div className="responsive-widget-inner">{children}</div>
    </div>
  );
}

export default ResponsiveWidgetWrapper;
